import {
	TreatmentPlan,
	TreatmentSegment,
} from "../../domain/treatmentPlan.interface";
import {
	defaultTemplateOf,
	readTemplateSegments,
} from "../../components/SegmentEditor/segmentEditor.helpers";

// SIMS-7487 (FR-19): pure helpers for deriving a program's duration from the
// segments of its treatment plan template.
//
// A program whose template is segmented no longer takes a manually entered
// duration — durationInWeeks is derived as the sum of the segment durations so
// program length can never diverge from the segmented structure. Non-segmented
// programs are untouched (AC4).
//
// Kept side-effect free so the rules are unit-testable in isolation from the MUI
// form (src/pages/Programs/index.tsx is excluded from jest coverage).

// AC2 (Hi-Fi): badge rendered inside the duration field when the value is derived.
export const AUTO_CALCULATED_BADGE_LABEL = "AUTO-CALCULATED";

// AC4 (Hi-Fi): caption under the duration field while it is still manually entered.
export const MANUAL_DURATION_HELPER_TEXT =
	"Enter the total program duration in weeks.";

// AC2 (Hi-Fi): the read-only segments summary panel beside the treatment plan picker.
export const SEGMENTS_PANEL_TITLE = "SEGMENTS (FROM TREATMENT PLAN)";
export const SEGMENTS_PANEL_FOOTER =
	"Read-only · sourced from the linked treatment plan";
export const SEGMENTS_PANEL_EMPTY_TEXT =
	"The linked treatment plan has no segments defined. Once segments are added, Duration in weeks becomes auto-calculated on the next save.";

// Look up the full template object for the id held in the program form state.
// The program form keeps only the ObjectId string, but the whole list (with
// treatmentConfig) is already in scope via useGetTreatmentPlans().
export const findTreatmentPlanById = (
	treatmentPlans: TreatmentPlan[] | undefined,
	treatmentPlanId: string | undefined,
): TreatmentPlan | undefined => {
	if (!treatmentPlans || !treatmentPlanId) {
		return undefined;
	}
	return treatmentPlans.find((plan) => plan._id === treatmentPlanId);
};

// The segments of the plan's DEFAULT template: its own list (SIMS-9222), else the
// plan-level list a plan saved before that change carries (treatmentConfig.segments).
export const templateSegments = (
	treatmentPlan: TreatmentPlan | undefined,
): TreatmentSegment[] =>
	readTemplateSegments(
		treatmentPlan?.treatmentConfig,
		defaultTemplateOf(treatmentPlan),
	);

// AC2/AC4: a template is "segmented" only when it actually carries at least one
// segment. No template, or a template with an absent/empty segments array, is
// non-segmented and keeps the manual duration input.
export const isSegmentedTemplate = (
	treatmentPlan: TreatmentPlan | undefined,
): boolean => templateSegments(treatmentPlan).length > 0;

// AC1: a segment spans its week range inclusively, so its duration is
// (end - start + 1) — a single week (start === end) counts as 1.
export const segmentDurationInWeeks = (segment: TreatmentSegment): number => {
	const start = segment.weekRange?.start;
	const end = segment.weekRange?.end;
	if (typeof start !== "number" || typeof end !== "number") {
		return 0;
	}
	const span = end - start + 1;
	return span > 0 ? span : 0;
};

// AC1: total derived duration = sum of the individual segment durations.
export const sumSegmentDurations = (
	segments: TreatmentSegment[] | undefined,
): number =>
	(segments ?? []).reduce(
		(total, segment) => total + segmentDurationInWeeks(segment),
		0,
	);

// AC1/AC4: the single resolution point used by both the rendered field and the
// submit payload, so the persisted value is authoritative regardless of how the
// form was reached.
//   - segmented template  -> derived sum (recomputed on every save, so editing a
//                            segment's week range recalculates the duration)
//   - anything else       -> the manually entered value, unchanged
export const resolveProgramDurationInWeeks = (
	treatmentPlan: TreatmentPlan | undefined,
	manualDurationInWeeks: number,
): number => {
	if (!isSegmentedTemplate(treatmentPlan)) {
		return manualDurationInWeeks;
	}
	return sumSegmentDurations(templateSegments(treatmentPlan));
};

// AC1 (Hi-Fi): the derived caption spells out the arithmetic rather than stating a
// generic rule, e.g.
//   "Derived from treatment plan segments: Segment 1 (4 wks) + Segment 2 (8 wks) = 12 weeks."
// Returns null when the template is not segmented — the manual caption is shown instead.
export const derivedDurationCaption = (
	treatmentPlan: TreatmentPlan | undefined,
): string | null => {
	const segments = templateSegments(treatmentPlan);
	if (segments.length === 0) {
		return null;
	}
	const parts = segments.map((segment, index) => {
		const weeks = segmentDurationInWeeks(segment);
		return `Segment ${index + 1} (${weeks} ${weeks === 1 ? "wk" : "wks"})`;
	});
	const total = sumSegmentDurations(segments);
	return `Derived from treatment plan segments: ${parts.join(" + ")} = ${total} ${
		total === 1 ? "week" : "weeks"
	}.`;
};

// AC2 (Hi-Fi): one row per segment in the read-only summary panel —
//   "Segment 1 · Weeks 1–4"  /  "3×/week"
export interface SegmentSummaryRow {
	label: string;
	cadence: string;
}

export const segmentSummaryRows = (
	treatmentPlan: TreatmentPlan | undefined,
): SegmentSummaryRow[] =>
	templateSegments(treatmentPlan).map((segment, index) => ({
		label: `Segment ${index + 1} · Weeks ${segment.weekRange?.start ?? 0}–${
			segment.weekRange?.end ?? 0
		}`,
		cadence: `${segment.visitsPerWeek}×/week`,
	}));

// AC2 (Hi-Fi): the treatment plan picker annotates each option with its segment
// state — "Phase Program A · 2 segments" / "Phase Program A · no segments yet" —
// so an admin can see which plans drive a derived duration before selecting one.
export const treatmentPlanOptionLabel = (
	treatmentPlan: TreatmentPlan,
): string => {
	const name =
		treatmentPlan.name || `Treatment Plan ${treatmentPlan._id.slice(-8)}`;
	const count = templateSegments(treatmentPlan).length;
	if (count === 0) {
		return `${name} · no segments yet`;
	}
	return `${name} · ${count} segment${count === 1 ? "" : "s"}`;
};
